import React, { useState, useEffect } from 'react';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
  Legend
} from 'recharts';
import ChartContainer from './ChartContainer';
import { formatLocalTime } from '../../services/timeUtils';
// Types are defined locally for this component

// Interface para as props do conteúdo customizado do LabelList
interface CustomLabelProps {
  x?: string | number;
  y?: string | number;
  width?: string | number;
  height?: string | number;
  value?: any;
  index?: number;
  offset?: number;
  position?: string;
  dataKey?: string;
}

interface DailyInteractionsChartProps {
  data: {
    dia: string;        // Dia da semana (Seg, Ter, etc.) ou dia do mês (01, 02, etc.)
    data: string;       // Data completa (ex: "2023-04-10")
    total_interacoes: number;
    total_interacoes_anterior?: number;
    porcentagem?: number;
  }[];
  loading?: boolean;
  mode?: 'weekly' | 'monthly'; // Novo parâmetro para especificar o modo
  title?: string; // Título personalizado opcional
}

// Definindo uma interface clara para os dados processados
interface ProcessedInteractionData {
  dia: string;
  data: string;        // Remover opcional para garantir compatibilidade
  total_interacoes: number;
  total_interacoes_anterior: number;  // Remover opcional
  porcentagem: number;                // Remover opcional
  percentage: number;                 // Remover opcional
  fill: string;
  isProjection?: boolean; // Este pode continuar opcional pois não causa problemas
}

function DailyInteractionsChart({
  data,
  loading = false,
  mode = 'weekly', // Default para compatibilidade com código existente
  title
}: DailyInteractionsChartProps) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  
  // Para debugging
  useEffect(() => {
    console.log(`DailyInteractionsChart (${mode}) recebeu dados:`, data);
    if (data && data.length > 0) {
      console.log('Amostra de dados recebidos:', JSON.stringify(data[0], null, 2));
      console.log('Total de itens recebidos:', data.length);
      console.log('Tem dados do período anterior?', data.some(item => item.total_interacoes_anterior !== undefined && item.total_interacoes_anterior > 0));
      
      // Verificar se há dias com zero interações
      const diasComZeroInteracoes = data.filter(item => item.total_interacoes === 0);
      if (diasComZeroInteracoes.length > 0) {
        console.warn('ALERTA: Ainda existem dias com zero interações nos dados recebidos:', 
          diasComZeroInteracoes.map(item => item.dia));
        console.warn('Estes dias deveriam ter sido filtrados no backend');
      } else {
        console.log('Todos os dias recebidos têm interações > 0, filtro aplicado corretamente');
      }
    } else {
      console.log('Nenhum dado recebido ou array vazio');
    }
  }, [data, mode]);
  
  // Função para obter cores para cada barra
  function getBarColor(dia: string, isProjection: boolean = false): string {
    if (isProjection) {
      return '#A1A1AA'; // Cor cinza para projeções
    }
    
    if (mode === 'weekly') {
      // Cores para dias da semana
      const weeklyColors: {[key: string]: string} = {
        'Dom': '#3B82F6', // Azul
        'Seg': '#4F46E5', // Índigo
        'Ter': '#7C3AED', // Violeta
        'Qua': '#8B5CF6', // Roxo
        'Qui': '#A78BFA', // Lilás
        'Sex': '#EC4899', // Rosa
        'Sáb': '#F43F5E'  // Vermelho
      };
      return weeklyColors[dia] || '#3B82F6';
    } else {
      // Escala de cores para dias do mês (mais escuro no início, mais claro no fim)
      const dayNum = parseInt(dia, 10);
      if (isNaN(dayNum)) return '#3B82F6';
      
      // Escala de azuis para os dias 1-31
      const baseHue = 220; // Azul
      
      // Ajuste da escala de cores - dividir o mês em grupos de cores
      // Primeira semana: tons de azul (220)
      // Segunda semana: tons de índigo/roxo (250-270)
      // Terceira semana: tons de roxo/rosa (280-300)
      // Quarta/Quinta semana: tons de vermelho/laranja (340-360)
      
      // Determinar o grupo de cores pelo dia
      let hue = 220; // Azul default
      if (dayNum <= 7) {
        hue = 220; // Azul
      } else if (dayNum <= 14) {
        hue = 260; // Índigo/Roxo
      } else if (dayNum <= 21) {
        hue = 290; // Roxo/Rosa
      } else {
        hue = 340; // Vermelho/Laranja
      }
      
      // Variação dentro do grupo (mais saturado ou menos saturado)
      const saturation = 70 + (dayNum % 7) * 3; // 70% a 88%
      
      // Lightness baseado no dia (alternando para criar contraste visual)
      const lightness = dayNum % 2 === 0 ? 55 : 45; // Alternância de claro e escuro
      
      return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
    }
  }
  
  // Dados de teste fixos para quando não há dados reais
  const dadosTeste = mode === 'weekly' ? [
    { dia: 'Dom', data: '2023-03-12', total_interacoes: 50, total_interacoes_anterior: 35, porcentagem: 43, percentage: 9, fill: getBarColor('Dom') },
    { dia: 'Seg', data: '2023-03-13', total_interacoes: 120, total_interacoes_anterior: 100, porcentagem: 20, percentage: 22, fill: getBarColor('Seg') },
    { dia: 'Ter', data: '2023-03-14', total_interacoes: 80, total_interacoes_anterior: 90, porcentagem: -11, percentage: 15, fill: getBarColor('Ter') },
    { dia: 'Qua', data: '2023-03-15', total_interacoes: 95, total_interacoes_anterior: 80, porcentagem: 19, percentage: 17, fill: getBarColor('Qua') },
    { dia: 'Qui', data: '2023-03-16', total_interacoes: 60, total_interacoes_anterior: 55, porcentagem: 9, percentage: 11, fill: getBarColor('Qui') },
    { dia: 'Sex', data: '2023-03-17', total_interacoes: 110, total_interacoes_anterior: 90, porcentagem: 22, percentage: 20, fill: getBarColor('Sex') },
    { dia: 'Sáb', data: '2023-03-18', total_interacoes: 40, total_interacoes_anterior: 50, porcentagem: -20, percentage: 7, fill: getBarColor('Sáb') }
  ] : [
    { dia: '01', data: '2023-04-01', total_interacoes: 150, total_interacoes_anterior: 135, porcentagem: 11, percentage: 5, fill: getBarColor('01') },
    { dia: '02', data: '2023-04-02', total_interacoes: 120, total_interacoes_anterior: 100, porcentagem: 20, percentage: 4, fill: getBarColor('02') },
    { dia: '03', data: '2023-04-03', total_interacoes: 280, total_interacoes_anterior: 250, porcentagem: 12, percentage: 10, fill: getBarColor('03') },
    { dia: '04', data: '2023-04-04', total_interacoes: 295, total_interacoes_anterior: 280, porcentagem: 5, percentage: 10, fill: getBarColor('04') },
    { dia: '05', data: '2023-04-05', total_interacoes: 260, total_interacoes_anterior: 255, porcentagem: 2, percentage: 9, fill: getBarColor('05') },
    { dia: '06', data: '2023-04-06', total_interacoes: 310, total_interacoes_anterior: 290, porcentagem: 7, percentage: 11, fill: getBarColor('06') },
    { dia: '07', data: '2023-04-07', total_interacoes: 340, total_interacoes_anterior: 250, porcentagem: 36, percentage: 12, fill: getBarColor('07') },
    { dia: '08', data: '2023-04-08', total_interacoes: 140, total_interacoes_anterior: 150, porcentagem: -7, percentage: 5, fill: getBarColor('08') },
    { dia: '09', data: '2023-04-09', total_interacoes: 110, total_interacoes_anterior: 100, porcentagem: 10, percentage: 4, fill: getBarColor('09') },
    { dia: '10', data: '2023-04-10', total_interacoes: 270, total_interacoes_anterior: 250, porcentagem: 8, percentage: 10, fill: getBarColor('10') }
  ];
  
  // Decisão se existe o dia específico que precisa de projeção
  function needsProjection(dados: any[], targetDay: string = 'Sáb'): boolean {
    // Usar o fuso horário local para obter o dia da semana
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 = Domingo, 6 = Sábado
    
    // Se hoje é sexta-feira (5) ou antes, e o dia alvo é sábado (6)
    const isFutureDay = (targetDay === 'Sáb' && dayOfWeek < 6) || 
                       (targetDay === 'Dom' && dayOfWeek < 0);
    
    // Verificar se o dia alvo está presente nos dados, mas sem interações
    const dayExists = dados.some(item => item.dia === targetDay);
    const dayHasNoData = dados.some(item => 
      item.dia === targetDay && 
      (item.total_interacoes === 0 || item.total_interacoes === undefined)
    );
    
    return isFutureDay || (dayExists && dayHasNoData);
  }
  
  // Função para criar projeção para dia futuro
  function createProjection(
    dados: ProcessedInteractionData[], 
    dayToProject: string = 'Sáb'
  ): ProcessedInteractionData | null {
    if (!dados || dados.length === 0) return null;
    
    // Verificar se já temos dados para o dia em questão
    const existingDay = dados.find(d => d.dia === dayToProject);
    if (existingDay && existingDay.total_interacoes > 0) return null;
    
    // Calcular média dos outros dias como base para projeção
    const otherDaysWithData = dados.filter(d => 
      d.dia !== dayToProject && 
      d.total_interacoes > 0
    );
    
    if (otherDaysWithData.length === 0) return null;
    
    // Média dos dias com dados
    const avgInteractions = otherDaysWithData.reduce(
      (sum, d) => sum + d.total_interacoes, 0
    ) / otherDaysWithData.length;
    
    // Dados da semana anterior para o dia alvo
    const prevWeekData = dados.find(d => 
      d.dia === dayToProject && 
      d.total_interacoes_anterior > 0
    );
    
    let projectedValue = 0;
    
    if (prevWeekData && prevWeekData.total_interacoes_anterior > 0) {
      // Se temos dados da semana anterior, usamos tendência da semana
      const weekTrend = dados.reduce((sum, d) => {
        if (d.total_interacoes > 0 && d.total_interacoes_anterior > 0) {
          return sum + (d.total_interacoes / d.total_interacoes_anterior);
        }
        return sum;
      }, 0) / otherDaysWithData.length;
      
      projectedValue = Math.round(prevWeekData.total_interacoes_anterior * weekTrend);
    } else {
      // Se não temos dados da semana anterior, usamos 90% da média dos outros dias 
      // (geralmente sábado/domingo tem menos interações)
      projectedValue = Math.round(avgInteractions * 0.9);
    }
    
    // Dados da semana anterior (usar o último elemento do array como referência)
    const lastDayIndex = dados.findIndex(d => d.dia === dayToProject);
    const lastDayData = lastDayIndex >= 0 ? dados[lastDayIndex] : null;
    
    // Calcular o percentual do total
    const totalInteractions = dados.reduce((sum, item) => sum + item.total_interacoes, 0);
    const percentage = totalInteractions > 0 ? Math.round((projectedValue / totalInteractions) * 100) : 0;
    
    // Criar objeto com todos os campos obrigatórios
    const today = new Date();
    const formattedDate = formatLocalTime(today, { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit',
      timeZone: 'America/Sao_Paulo'
    });
    
    return {
      dia: dayToProject,
      data: formattedDate,
      total_interacoes: projectedValue,
      total_interacoes_anterior: lastDayData?.total_interacoes_anterior || 0,
      porcentagem: lastDayData?.porcentagem || 0,
      percentage: percentage,
      fill: getBarColor(dayToProject, true),
      isProjection: true
    };
  }
  
  // Função para processar os dados recebidos
  const processData = (rawData: any[], mode: 'weekly' | 'monthly'): ProcessedInteractionData[] => {
    if (!rawData || rawData.length === 0) {
      console.log(`Sem dados para processar no modo ${mode}`);
      return [];
    }

    console.log(`Processando ${rawData.length} itens de dados no modo ${mode}`);
    console.log('Primeiro item dos dados brutos:', rawData[0]);
    
    // Verificar se temos dados da semana anterior
    const temDadosAnteriores = rawData.some(item => 
      item.total_interacoes_anterior !== undefined && 
      item.total_interacoes_anterior > 0
    );
    
    console.log(`Dados contêm informações da ${mode === 'weekly' ? 'semana' : 'mês'} anterior? ${temDadosAnteriores}`);
    
    // Calcular o total para percentuais
    const totalInteracoes = rawData.reduce((sum, item) => sum + (item.total_interacoes || 0), 0);
    
    return rawData.map(item => {
      // Garantir que temos uma data válida
      let dataFormatada = item.data;
      
      if (dataFormatada && typeof dataFormatada === 'string') {
        // Converter para objeto Date e formatar corretamente para o fuso horário local
        try {
          console.log(`Processando data: ${dataFormatada}, tipo: ${typeof dataFormatada}`);
          
          // Verificar se a data está em um formato válido
          // Se a data estiver no formato DD (dia do mês), precisamos construir uma data completa
          if (/^\d{1,2}$/.test(dataFormatada)) {
            console.log(`Data parece ser apenas o dia do mês: ${dataFormatada}`);
            // Usar o mês e ano atuais para construir uma data completa
            const hoje = new Date();
            dataFormatada = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}-${String(dataFormatada).padStart(2, '0')}`;
            console.log(`Data reconstruída: ${dataFormatada}`);
          }
          
          const dataObj = new Date(dataFormatada);
          
          // Verificar se a data é válida
          if (isNaN(dataObj.getTime())) {
            console.warn(`Data inválida: ${dataFormatada}`);
            // Usar a data atual como fallback
            dataFormatada = formatLocalTime(new Date(), { 
              year: 'numeric', 
              month: '2-digit', 
              day: '2-digit',
              timeZone: 'America/Sao_Paulo'
            });
          } else {
            console.log(`Data convertida para objeto Date: ${dataObj.toISOString()}`);
            dataFormatada = formatLocalTime(dataObj, { 
              year: 'numeric', 
              month: '2-digit', 
              day: '2-digit',
              timeZone: 'America/Sao_Paulo'
            });
            console.log(`Data formatada: ${dataFormatada}`);
          }
        } catch (e) {
          console.warn('Erro ao formatar data:', e);
          // Usar a data atual como fallback
          dataFormatada = formatLocalTime(new Date(), { 
            year: 'numeric', 
            month: '2-digit', 
            day: '2-digit',
            timeZone: 'America/Sao_Paulo'
          });
        }
      } else {
        console.warn(`Data inválida ou ausente: ${dataFormatada}, tipo: ${typeof dataFormatada}`);
        // Usar a data atual como fallback
        dataFormatada = formatLocalTime(new Date(), { 
          year: 'numeric', 
          month: '2-digit', 
          day: '2-digit',
          timeZone: 'America/Sao_Paulo'
        });
      }
      
      // Calcular o percentual do total
      const percentage = totalInteracoes > 0 
        ? Math.round(((item.total_interacoes || 0) / totalInteracoes) * 100) 
        : 0;
      
      // Garantir que total_interacoes_anterior seja um número válido
      let totalAnterior = item.total_interacoes_anterior;
      if (totalAnterior === null || totalAnterior === undefined) {
        totalAnterior = 0;
        console.log(`Valor nulo/indefinido para total_interacoes_anterior no dia ${item.dia}, definindo como 0`);
      } else {
        totalAnterior = Number(totalAnterior);
        if (isNaN(totalAnterior)) {
          console.warn(`Valor inválido para total_interacoes_anterior no dia ${item.dia}: ${item.total_interacoes_anterior}`);
          totalAnterior = 0;
        }
      }
      
      return {
        dia: item.dia || '',
        data: dataFormatada,
        total_interacoes: item.total_interacoes || 0,
        total_interacoes_anterior: totalAnterior,
        porcentagem: item.porcentagem || 0,
        percentage,
        fill: getBarColor(item.dia || '', false)
      };
    });
  };

  // Processar dados recebidos
  const dadosFinais = React.useMemo<ProcessedInteractionData[]>(() => {
    // Se não temos dados, usar teste
    if (!data || data.length === 0) {
      console.log(`Usando dados de teste para o modo ${mode} (sem dados reais recebidos)`);
      return dadosTeste;
    }
    
    console.log('Usando dados reais da API');
    console.log('Dados brutos recebidos:', JSON.stringify(data, null, 2));
    
    // Verificar se temos dados da semana anterior
    const temDadosAnteriores = data.some(item => 
      item.total_interacoes_anterior !== undefined && 
      item.total_interacoes_anterior > 0
    );
    console.log(`Dados contêm informações da ${mode === 'weekly' ? 'semana' : 'mês'} anterior? ${temDadosAnteriores}`);
    
    // Verificar especificamente os dados do sábado
    const sabadoData = data.find(item => item.dia === 'Sáb');
    console.log('Dados do sábado nos dados brutos:', 
      sabadoData ? JSON.stringify(sabadoData, null, 2) : 'Sábado não encontrado nos dados');
    
    if (sabadoData) {
      console.log('Sábado - total_interacoes:', sabadoData.total_interacoes);
      console.log('Sábado - total_interacoes_anterior:', sabadoData.total_interacoes_anterior);
      console.log('Sábado - porcentagem:', sabadoData.porcentagem);
    }
    
    // Verificar dias com zero interações na semana atual
    const diasSemDadosAtuais = data.filter(item => item.total_interacoes === 0);
    if (diasSemDadosAtuais.length > 0) {
      console.log('Dias sem dados na semana atual:', diasSemDadosAtuais.map(item => item.dia));
      console.log('Estes dias deveriam ser filtrados no backend e não aparecer no gráfico');
    }
    
    if (!temDadosAnteriores) {
      console.warn(`Nenhum dado da ${mode === 'weekly' ? 'semana' : 'mês'} anterior encontrado!`);
    } else {
      console.log('Dados anteriores encontrados:', data.map(item => ({
        dia: item.dia,
        atual: item.total_interacoes,
        anterior: item.total_interacoes_anterior
      })));
    }
    
    // Processar os dados usando a função centralizada
    const processedData = processData(data, mode);
    console.log('Dados processados:', JSON.stringify(processedData, null, 2));
    
    // Verificar os dados do sábado após processamento
    const sabadoProcessado = processedData.find(item => item.dia === 'Sáb');
    console.log('Dados do sábado após processamento:', 
      sabadoProcessado ? JSON.stringify(sabadoProcessado, null, 2) : 'Sábado não encontrado após processamento');
    
    // Verificar se precisamos adicionar projeção
    if (mode === 'weekly' && needsProjection(processedData)) {
      const projection = createProjection(processedData);
      if (projection) {
        console.log('Adicionando projeção para:', projection.dia);
        console.log('Dados da projeção:', JSON.stringify(projection, null, 2));
        return [...processedData, projection];
      }
    }
    
    return processedData;
  }, [data, mode]);
  
  // Calcular o total semanal atual
  const totalSemanal = React.useMemo<number>(() => {
    if (!dadosFinais || dadosFinais.length === 0) return 0;
    
    // Ignorar projeções no cálculo do total, a menos que não tenhamos dados reais
    const hasRealData = dadosFinais.some(item => !item.isProjection && item.total_interacoes > 0);
    
    const total = dadosFinais.reduce((sum: number, item: ProcessedInteractionData) => {
      // Se temos dados reais, ignorar projeções. Caso contrário, usar tudo
      if (hasRealData && item.isProjection) return sum;
      return sum + item.total_interacoes;
    }, 0);
    
    console.log('Total calculado atual:', total);
    return total;
  }, [dadosFinais]);
  
  // Calcular o total da semana anterior
  const totalSemanaAnterior = React.useMemo<number>(() => {
    if (!dadosFinais || dadosFinais.length === 0) return 0;
    
    const total = dadosFinais.reduce((sum: number, item: ProcessedInteractionData) => {
      return sum + (item.total_interacoes_anterior || 0);
    }, 0);
    
    console.log('Total calculado semana anterior:', total);
    
    // Verificar se temos pelo menos um dia com dados da semana anterior
    const temDadosAnteriores = dadosFinais.some(item => item.total_interacoes_anterior > 0);
    console.log('Temos pelo menos um dia com dados da semana anterior?', temDadosAnteriores);
    
    return total;
  }, [dadosFinais]);
  
  // Calcular a variação percentual entre as semanas
  const variacaoPercentual = React.useMemo<number>(() => {
    if (totalSemanaAnterior === 0) return 0;
    return Math.round(((totalSemanal - totalSemanaAnterior) / totalSemanaAnterior) * 100);
  }, [totalSemanal, totalSemanaAnterior]);
  
  // Verificar se temos dados da semana anterior para exibir a linha
  const temDadosSemanaAnterior = React.useMemo(() => {
    if (!dadosFinais || dadosFinais.length === 0) return false;
    return dadosFinais.some(item => item.total_interacoes_anterior > 0);
  }, [dadosFinais]);

  // Encontrar o valor máximo para o eixo Y
  const maxInteractions = React.useMemo<number>(() => {
    if (!dadosFinais || dadosFinais.length === 0) return 100;
    
    const allValues = dadosFinais.flatMap(item => [
      item.total_interacoes,
      item.total_interacoes_anterior || 0
    ]);
    
    const maxValue = Math.max(...allValues);
    return maxValue <= 0 ? 100 : 
           maxValue <= 100 ? 100 : 
           maxValue <= 500 ? Math.ceil(maxValue / 100) * 100 : 
           maxValue <= 1000 ? 1000 : 
           Math.ceil(maxValue / 1000) * 1000;
  }, [dadosFinais]);

  const handleMouseOver = (data: any, index: number) => {
    setActiveIndex(index);
  };

  const handleMouseLeave = () => {
    setActiveIndex(null);
  };

  // Formatar números grandes de forma legível
  const formatNumber = (num: any): string => {
    if (num === null || num === undefined || isNaN(Number(num))) return '0';
    return new Intl.NumberFormat('pt-BR').format(Number(num));
  };

  const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: any[]; label?: string | number }) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      
      // Validar a data antes de tentar formatá-la
      let dateString = '';
      if (item?.data && typeof item.data === 'string') {
        try {
          // Verificar se a data é válida antes de tentar formatá-la
          const dataObj = new Date(item.data);
          if (!isNaN(dataObj.getTime())) {
            dateString = formatLocalTime(dataObj, {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              timeZone: 'America/Sao_Paulo'
            });
          } else {
            console.warn(`Data inválida no tooltip: ${item.data}`);
            dateString = item.data; // Usar a string original como fallback
          }
        } catch (e) {
          console.error('Erro ao formatar data no tooltip:', e);
          dateString = item.data; // Usar a string original como fallback
        }
      }
      
      return (
        <div className="bg-gray-800 p-3 border border-gray-700 rounded shadow-lg">
          <p className="text-sm font-medium text-gray-300">{label}</p>
          {dateString && <p className="text-xs text-gray-400">{dateString}</p>}
          
          {/* Indicador de projeção se aplicável */}
          {item.isProjection && (
            <p className="text-xs text-gray-300 mt-1 italic">
              <span className="bg-gray-700 text-gray-300 px-1 py-0.5 rounded text-xs mr-1">PROJEÇÃO</span>
              Valor estimado baseado na tendência da {mode === 'weekly' ? 'semana' : 'mês'}
            </p>
          )}
          
          {/* Interações desta semana/mês */}
          <p className="text-sm flex items-center" style={{ color: item.fill || '#3B82F6' }}>
            <span className="w-3 h-3 inline-block rounded-full mr-2" style={{ backgroundColor: item.fill || '#3B82F6' }}></span>
            <span className="font-medium">{mode === 'weekly' ? 'Esta semana' : 'Este mês'}:</span>{' '}
            {formatNumber(item.total_interacoes)}
          </p>
          
          {/* Interações da semana/mês anterior */}
          {item.total_interacoes_anterior !== undefined && item.total_interacoes_anterior > 0 && (
            <p className="text-sm flex items-center" style={{ color: '#10B981' }}>
              <span className="w-3 h-3 inline-block rounded-full mr-2" style={{ backgroundColor: '#10B981' }}></span>
              <span className="font-medium">{mode === 'weekly' ? 'Semana anterior' : 'Mês anterior'}:</span>{' '}
              {formatNumber(item.total_interacoes_anterior)}
            </p>
          )}
          
          {/* Comparação percentual com a semana/mês anterior */}
          {item.porcentagem !== undefined && (
            <p className="text-xs text-gray-400 mt-1">
              {item.porcentagem > 0 
                ? `+${item.porcentagem}% vs ${mode === 'weekly' ? 'semana' : 'mês'} anterior` 
                : `${item.porcentagem}% vs ${mode === 'weekly' ? 'semana' : 'mês'} anterior`}
            </p>
          )}
          
          {/* Porcentagem do total semanal/mensal */}
          {item.percentage !== undefined && (
            <p className="text-sm text-blue-400 mt-1">
              <span className="font-medium">Contribuição:</span>{' '}
              {item.percentage}% do total {mode === 'weekly' ? 'semanal' : 'mensal'}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  // Função para renderizar o rótulo da porcentagem apenas se for maior que zero
  const renderCustomizedLabel = (props: CustomLabelProps) => {
    const { x, y, width, value } = props;
    if (isNaN(Number(value)) || Number(value) <= 0) return null;
    
    const xPos = typeof x === 'number' ? x : Number(x);
    const yPos = typeof y === 'number' ? y : Number(y);
    const widthVal = typeof width === 'number' ? width : Number(width);
    
    return (
      <text 
        x={!isNaN(xPos) && !isNaN(widthVal) ? xPos + widthVal / 2 : 0} 
        y={!isNaN(yPos) ? yPos - 6 : 0} 
        fill="#9CA3AF" 
        textAnchor="middle" 
        fontSize={12}
        fontWeight={600}
        style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
      >
        {`${value}%`}
      </text>
    );
  };
  
  // Função para renderizar os rótulos de valor nas barras do modo mensal
  const renderMonthlyBarValueLabel = (props: CustomLabelProps) => {
    const { x, y, width, value, index } = props;
    
    // Se não tiver valor ou for valor pequeno, não mostrar label
    if (!value || Number(value) < 100) return null;
    
    // Em dias alternados, mostrar ou não o label
    if (typeof index === 'number' && index % 2 !== 0) return null;
    
    const xPos = typeof x === 'number' ? x : Number(x);
    const yPos = typeof y === 'number' ? y : Number(y);
    const widthVal = typeof width === 'number' ? width : Number(width);
    
    return (
      <text 
        x={!isNaN(xPos) && !isNaN(widthVal) ? xPos + widthVal / 2 : 0} 
        y={!isNaN(yPos) ? yPos - 6 : 0} 
        fill="#e5e7eb" 
        textAnchor="middle" 
        fontSize="12"
      >
        {typeof value === 'number' ? formatNumber(value) : formatNumber(Number(value) || 0)}
      </text>
    );
  };

  // Determinar o título com base no modo
  const chartTitle = title || (mode === 'weekly' 
    ? 'Interações Diárias da Semana' 
    : 'Interações Diárias do Mês');

  // Determinar texto comparativo adequado
  const comparisonText = mode === 'weekly' 
    ? 'Semana Anterior' 
    : 'Mês Anterior';

  return (
    <ChartContainer 
      title={chartTitle}
      tooltip={`Comparação de interações diárias entre ${mode === 'weekly' ? 'a semana atual e a anterior' : 'o mês atual e o anterior'}.`}
      headerContent={
        <div className="text-xs bg-blue-500/20 text-blue-300 py-1 px-2 rounded-full">
          {mode === 'weekly' 
            ? 'Dados da semana atual vs. semana anterior'
            : 'Dados do mês atual vs. mês anterior'}
        </div>
      }
    >
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : dadosFinais.length > 0 ? (
        <ResponsiveContainer width="100%" height={350}>
          <ComposedChart 
            data={dadosFinais}
            margin={{ top: 20, right: 30, left: 20, bottom: mode === 'monthly' ? 30 : 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis 
              dataKey="dia" 
              tick={{ fill: '#e5e7eb' }}
              interval={mode === 'monthly' ? 'preserveStartEnd' : 0}
              angle={mode === 'monthly' ? -45 : 0}
              tickMargin={mode === 'monthly' ? 10 : 5}
              height={mode === 'monthly' ? 60 : 30}
              textAnchor={mode === 'monthly' ? 'end' : 'middle'}
              minTickGap={mode === 'monthly' ? 5 : 0}
            />
            <YAxis 
              yAxisId="left"
              orientation="left"
              tick={{ fill: '#e5e7eb' }}
            />
            <YAxis 
              yAxisId="right"
              orientation="right"
              tick={{ fill: '#e5e7eb' }}
            />
            <Tooltip content={CustomTooltip} />
            <Legend 
              formatter={(value) => {
                if (value === 'total_interacoes') return `${mode === 'weekly' ? 'Semana' : 'Mês'} Atual`;
                if (value === 'total_interacoes_anterior') return comparisonText;
                return value;
              }}
            />
            <Bar
              dataKey="total_interacoes"
              name="total_interacoes"
              yAxisId="left"
              onMouseOver={handleMouseOver}
              onMouseLeave={handleMouseLeave}
              barSize={mode === 'monthly' ? 15 : 20}
            >
              {dadosFinais.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={entry.fill || '#3B82F6'} 
                  fillOpacity={index === activeIndex ? 1 : 0.8}
                  stroke={index === activeIndex ? '#fff' : 'none'}
                  strokeWidth={index === activeIndex ? 1 : 0}
                />
              ))}
              {mode === 'monthly' ? (
                // No modo mensal, usamos lógica personalizada para evitar sobreposição de labels
                <LabelList 
                  dataKey="total_interacoes" 
                  position="top" 
                  formatter={formatNumber}
                  style={{ fill: '#e5e7eb', fontSize: '12px' }}
                  content={renderMonthlyBarValueLabel as any}
                />
              ) : (
                // No modo semanal, usamos o LabelList padrão
                <LabelList 
                  dataKey="total_interacoes" 
                  position="top" 
                  formatter={formatNumber}
                  style={{ fill: '#e5e7eb', fontSize: '12px' }}
                />
              )}
            </Bar>
            {temDadosSemanaAnterior && (
              <Line
                type="monotone"
                dataKey="total_interacoes_anterior"
                name="total_interacoes_anterior"
                stroke="#FCD34D"
                strokeWidth={2}
                dot={{ fill: '#FCD34D', r: 4 }}
                activeDot={{ fill: '#FBBF24', r: 6, stroke: '#FBBF24' }}
                yAxisId="right"
                connectNulls={true}
                isAnimationActive={true}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      ) : (
        <div className="flex justify-center items-center h-64 text-gray-400">
          <p>Nenhum dado disponível para o período selecionado.</p>
        </div>
      )}
    </ChartContainer>
  );
}

export default DailyInteractionsChart; 